/**
 * Maps a persisted crawl failure code to its i18n key.
 *
 * `project_artifacts.analysis_error` holds a machine code for `link` Sources
 * (`crawl_timeout`, `crawl_forbidden_robots`…), never a human sentence. Every
 * surface that displays it — the viewer banner and the card's status tooltip —
 * has to translate it, so the table lives here rather than being duplicated
 * per component.
 */
import { isLinkCrawlErrorCode } from '@/types/projectAnalysis';
import type { LinkCrawlErrorCode } from '@/types/projectAnalysis';

/** `crawl_failed` is the untyped catch-all: it shares the generic message. */
const CODE_TO_I18N_SUFFIX: Record<LinkCrawlErrorCode, string> = {
  crawl_unreachable: 'unreachable',
  crawl_timeout: 'timeout',
  crawl_forbidden_robots: 'robots',
  crawl_forbidden_host: 'forbiddenHost',
  crawl_requires_auth: 'requiresAuth',
  crawl_not_html: 'notHtml',
  crawl_empty_content: 'empty',
  crawl_failed: 'generic'
};

/**
 * Resolves the i18n key for a persisted `analysis_error` value.
 *
 * @param code - Raw `analysis_error` column value.
 * @returns The `projects.artifacts.crawlErrors.*` key, or `null` when the
 *   value is not a typed crawl code (a legacy free-text message, for
 *   instance). Callers pick their own fallback rather than ever rendering the
 *   raw value — a machine code or a driver message is never shown as-is.
 */
export const crawlErrorI18nKey = (code: string | null | undefined): string | null => {
  if (!isLinkCrawlErrorCode(code)) return null;
  return `projects.artifacts.crawlErrors.${CODE_TO_I18N_SUFFIX[code]}`;
};
